/**
 * @file
 * The Disclosure class.
 */

import { isValidClassList, isValidType, isTag } from "../validate.js";
import { addClass, removeClass } from "../domHelpers.js";
import { keyPress, preventEvent } from "../eventHandlers.js";
import { TransactionalValue } from "../TransactionalValue.js";
import Component from "../Component.js";

class Disclosure extends Component {
  /**
   * The DOM elements within the disclosure.
   *
   * @protected
   *
   * @type {Object<HTMLElement>}
   *
   * @property {HTMLElement} controller - The disclosure toggle element.
   * @property {HTMLElement} disclosure - The disclosure element.
   * @property {HTMLElement} content    - The disclosure content element.
   */
  _dom = {
    controller: null,
    disclosure: null,
    content: null,
  };

  /**
   * The DOM elements within the disclosure that cannot be reset or generated by the disclosure itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = ["controller", "disclosure"];

  /**
   * The query selectors used by the disclosure.
   *
   * @protected
   *
   * @type {Object<string>}
   *
   * @property {string} content - The query selector for the disclosure content.
   */
  _selectors = {
    content: "",
  };

  /**
   * The CSS classes to apply when the disclosure is in various states.
   *
   * @protected
   *
   * @type {Object<string, string[]>}
   *
   * @property {string|string[]} open       - The class(es) to apply when the disclosure is open.
   * @property {string|string[]} close      - The class(es) to apply when the disclousre is closed.
   * @property {string|string[]} tranistion - The class(es) to apply when the disclosure is transitioning between states.
   * @property {string|string[]} initialize - The class(es) to apply when the disclosure is initializing.
   */
  _classes = {
    open: "show",
    close: "hide",
    transition: "transitioning",
    initialize: "initializing",
  };

  /**
   * The duration times (in milliseconds) for variouse aspects throughout the disclosure.
   *
   * @protected
   *
   * @type {Object<number>}
   *
   * @property {number} transition - The duration time (in milliseconds) for the transition between open and closed states.
   * @property {number} open       - The duration time (in milliseconds) for the transition from closed to open states.
   * @property {number} close      - The duration time (in milliseconds) for the transition from open to closed states.
   */
  _durations = {
    transition: 5000,
    open: -1,
    close: -1,
  };

  /**
   * The open state of the disclosure.
   *
   * @protected
   *
   * @type {TransactionalValue<boolean>}
   */
  _open = new TransactionalValue(false);

  /**
   * A value to force the disclosure open when the breakpoint width is passed.
   *
   * @protected
   *
   * @type {boolean}
   */
  _shouldOpen = false;

  /**
   * Whether or not to close the disclosure when it loses focus in the DOM.
   *
   * @protected
   *
   * @type {boolean}
   */
  _closeOnBlur = false;

  /**
   * A callback for media query list events.
   *
   * @protected
   *
   * @type {Function}
   *
   * @param {MediaQueryListEvent} event - The event.
   */
  _mediaQueryListEventCallback = (event) => {
    if (event.matches && this.isOpen) {
      this.close({ preserveState: true });
    } else if (
      !event.matches &&
      !this.isOpen &&
      (this.hasOpened || this.shouldOpen)
    ) {
      this.open();
    }
  };

  /**
   * The key used for storage.
   *
   * @protected
   *
   * @type {string}
   */
  _storageKey = "disclosures";

  /**
   * Custom events that can be triggered throughout the disclosure.
   *
   * @protected
   *
   * @type {Object<CustomEvent>}
   */
  _events = {
    /**
     * The event that is triggered when the disclosure is shown.
     *
     * @event grauplDisclosureExpand
     *
     * @type {CustomEvent}
     *
     * @property {boolean}            bubbles           - A flag to bubble the event
     * @property {Object<Disclosure>} detail            - The details object containing the disclosure itself.
     * @property {Disclosure}         detail.disclosure - The disclosure.
     */
    expand: new CustomEvent("grauplDisclosureExpand", {
      bubbles: true,
      detail: {
        disclosure: this,
      },
    }),
    /**
     * The event that is triggered when the disclosure is hidden.
     *
     * @event grauplDisclosureCollapse
     *
     * @type {CustomEvent}
     *
     * @property {boolean}            bubbles           - A flag to bubble the event
     * @property {Object<Disclosure>} detail            - The details object containing the disclosure itself.
     * @property {Disclosure}         detail.disclosure - The disclosure.
     */
    collapse: new CustomEvent("grauplDisclosureCollapse", {
      bubbles: true,
      detail: {
        disclosure: this,
      },
    }),
  };

  /**
   * Constructs a new `Disclosure`.
   *
   * @param {object}             [options = {}]                                            - The options for generating the disclosure.
   * @param {HTMLElement}        options.disclosureElement                                 - The disclosure element in the DOM.
   * @param {HTMLElement}        options.controllerElement                                 - The disclosure toggle element in the DOM.
   * @param {string}             [options.disclosureContentSelector = .disclosure-content] - The query selector string for the disclosure content.
   * @param {?(string|string[])} [options.openClass = show]                                - The class to apply when a disclosure is "open".
   * @param {?(string|string[])} [options.closeClass = hide]                               - The class to apply when a disclosure is "closed".
   * @param {?(string|string[])} [options.transitionClass = transitioning]                 - The class to apply when a disclosure is transitioning between "open" and "closed" states.
   * @param {number}             [options.transitionDuration = 250]                        - The duration of the transition between "open" and "closed" states (in milliseconds).
   * @param {boolean}            [options.openDuration = -1]                               - The duration of the transition from "closed" to "open" states (in milliseconds).
   * @param {boolean}            [options.closeDuration = -1]                              - The duration of the transition from "open" to "closed" states (in milliseconds).
   * @param {boolean}            [options.closeOnBlur = false]                             - Whether to close the disclosure when it loses focus in the dom.
   * @param {?string}            [options.minWidth = ""]                                   - The width of the screen that the disclosure will automatically open/close itself.
   * @param {boolean}            [options.autoOpen = false]                                - Whether to automatically open when above the minWidth.
   * @param {?string}            [options.mediaQuery = ""]                                 - The media query to use when automatically opening/closing the disclosure.
   * @param {?string}            [options.prefix = graupl-]                                - The prefix used for CSS custom properties and attributes.
   * @param {?string}            [options.key = null]                                      - The key used to generate IDs throughout the disclosure.
   * @param {?(string|string[])} [options.initializeClass = initializing]                  - The class(es) to apply when the disclosure is initializing.
   * @param {boolean}            [options.initialize = false]                              - A flag to initialize the disclosure immediately upon creation.
   */
  constructor({
    disclosureElement,
    controllerElement,
    disclosureContentSelector = ".disclosure-content",
    openClass = "show",
    closeClass = "hide",
    transitionClass = "transitioning",
    transitionDuration = 250,
    openDuration = -1,
    closeDuration = -1,
    closeOnBlur = false,
    minWidth = "",
    autoOpen = false,
    mediaQuery = "",
    prefix = "graupl-",
    key = null,
    initializeClass = "initializing",
    initialize = false,
  } = {}) {
    super({
      prefix,
      key,
      initializeClass,
    });

    // Set the DOM elements.
    this._dom.disclosure = disclosureElement;
    this._dom.controller = controllerElement;

    // Set the DOM selectors.
    this._selectors.content = disclosureContentSelector;

    // Set the classes.
    this._classes.open = openClass || "";
    this._classes.close = closeClass || "";
    this._classes.transition = transitionClass || "";

    // Set the durations.
    this._durations.transition = transitionDuration;
    this._durations.open = openDuration;
    this._durations.close = closeDuration;

    // Set close on blur.
    this._closeOnBlur = closeOnBlur;

    // Set collapse width and auto open functionality.
    this._breakpoint = minWidth || "";
    this._shouldOpen = autoOpen;
    this._mediaQueryString = mediaQuery || "";

    if (initialize) {
      this.initialize();
    }
  }

  /**
   * Initializes the disclosure.
   */
  initialize() {
    try {
      // Add the initialization class.
      addClass(this._classes.initialize, this.dom.disclosure);

      super.initialize();

      // Set up the DOM.
      this._setDOMElements();
      this._setIds();
      this._setAriaAttributes();
      this._setCustomProps();

      // Handle events.
      this._handleMediaMatch();
      this._handleFocus();
      this._handleClick();
      this._handleKeydown();
      this._handleKeyup();

      // Store the component.
      this._store();

      // Handle auto-opening disclsoures with aria-expanded set to true or
      // those that _should_ open.
      if (
        this.dom.controller.getAttribute("aria-expanded") === "true" ||
        (this.shouldOpen && window.matchMedia(this.mediaQuery).matches)
      ) {
        this._expand({ emit: false, transition: false });
      } else {
        this._collapse({ emit: false, transition: false });
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Remove the initialization class.
      requestAnimationFrame(() => {
        removeClass(this._classes.initialize, this.dom.disclosure);
      });
    }
  }

  /**
   * The class(es) to apply when the disclosure is open.
   *
   * @type {string|string[]}
   *
   * @see _classes.open
   */
  get openClass() {
    return this._classes.open;
  }

  set openClass(value) {
    isValidClassList({ openClass: value });

    if (this._classes.open !== value) {
      this._classes.open = value;
    }
  }

  /**
   * The class(es) to apply when the disclosure is closed.
   *
   * @type {string|string[]}
   *
   * @see _classes.close
   */
  get closeClass() {
    return this._classes.close;
  }

  set closeClass(value) {
    isValidClassList({ closeClass: value });

    if (this._classes.close !== value) {
      this._classes.close = value;
    }
  }

  /**
   * The class(es) to apply when the disclosure is transitioning between open and closed.
   *
   * @type {string|string[]}
   *
   * @see _classes.transition
   */
  get transitionClass() {
    return this._classes.transition;
  }

  set transitionClass(value) {
    isValidClassList({ transitionClass: value });

    if (this._classes.transition !== value) {
      this._classes.transition = value;
    }
  }

  /**
   * The duration time (in milliseconds) for the transition between open and closed states.
   *
   * Setting this value will also set the --graupl-transition-duration CSS custom property on the disclosure.
   *
   * @type {number}
   *
   * @see _durations.transition
   */
  get transitionDuration() {
    return this._durations.transition;
  }

  set transitionDuration(value) {
    isValidType("number", { transitionDuration: value });

    if (this._durations.transition !== value) {
      this._durations.transition = value;
      this._setCustomProps();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from closed to open states.
   *
   * If openDuration is set to -1, the transitionDuration value will be used instead.
   *
   * Setting this value will also set the --graupl-open-transition-duration CSS custom property on the disclosure.
   *
   * @type {number}
   *
   * @see _durations.open
   */
  get openDuration() {
    if (this._durations.open === -1) return this.transitionDuration;

    return this._durations.open;
  }

  set openDuration(value) {
    isValidType("number", { openDuration: value });

    if (this._durations.open !== value) {
      this._durations.open = value;
      this._setCustomProps();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from open to closed states.
   *
   * If closeDuration is set to -1, the transitionDuration value will be used instead.
   *
   * Setting this value will also set the --graupl-close-transition-duration CSS custom property on the disclosure.
   *
   * @type {number}
   *
   * @see _durations.close
   */
  get closeDuration() {
    if (this._durations.close === -1) return this.transitionDuration;

    return this._durations.close;
  }

  set closeDuration(value) {
    isValidType("number", { closeDuration: value });

    if (this._durations.close !== value) {
      this._durations.close = value;
      this._setCustomProps();
    }
  }

  /**
   * The width of the screen that the disclosure will automatically open/close itself.
   *
   * This is just an alias for the generic "breakpoint" used in all components.
   *
   * @type {string}
   *
   * @see breakpoint
   */
  get minWidth() {
    return this.breakpoint;
  }

  set minWidth(value) {
    this.breakpoint = value;
  }

  /**
   * Whether to close the disclosure when it loses focus in the DOM.
   *
   * @type {boolean}
   *
   * @see _closeOnBlur
   */
  get closeOnBlur() {
    return this._closeOnBlur;
  }

  set closeOnBlur(value) {
    isValidType("boolean", { closeOnBlur: value });

    if (this._closeOnBlur !== value) {
      this._closeOnBlur = value;
    }
  }

  /**
   * The open state of the disclosure.
   *
   * @readonly
   *
   * @type {boolean}
   *
   * @see _open
   */
  get isOpen() {
    return this._open.value;
  }

  /**
   * The open state of the disclosure that the user specifically triggered.
   *
   * @readonly
   *
   * @type {boolean}
   *
   * @see _open
   */
  get hasOpened() {
    return this._open.committed;
  }

  /**
   * A value to force opening reguardless of user interaction.
   *
   * @type {boolean}
   *
   * @see _shouldOpen
   */
  get shouldOpen() {
    return this._shouldOpen;
  }

  set shouldOpen(value) {
    isValidType("boolean", { shouldOpen: value });

    if (this._shouldOpen !== value) {
      this._shouldOpen = value;
    }
  }

  /**
   * A flag to check if the disclosure's focus methods should _actually_ move the focus in the DOM.
   *
   * This will be `false` unless any of the following criteria are met:
   * - The disclosure's current event is "keyboard".
   *
   * @readonly
   *
   * @type {boolean}
   */
  get shouldFocus() {
    let check = false;

    if (this.currentEvent === "keyboard") {
      check = true;
    }

    return check;
  }

  /**
   * Validates all aspects of the disclosure to ensure proper functionality.
   *
   * Keys are altered to match the arguments passed in during creation where possible.
   *
   * @protected
   *
   * @return {boolean} - The result of the validation.
   */
  _validate() {
    super._validate();

    // Boolean checks.
    const booleans = {
      closeOnBlur: this._closeOnBlur,
      autoOpen: this._shouldOpen,
    };

    // Check the booleans.
    const booleanChecks = isValidType("boolean", booleans);

    // Handle boolean check failure.
    if (!booleanChecks) {
      this._errors.push(booleanChecks.message);
      this._valid = false;
    }

    return this._valid;
  }

  /**
   * Sets the IDs throughout the disclosure.
   *
   * The generated IDs use the key and follow the format:
   *  - disclosure: `disclosure-${key}`
   *  - controller: `disclosure-controller-${key}`
   *
   * @protected
   */
  _setIds() {
    this.dom.disclosure.id = this.dom.disclosure.id || `disclosure-${this.key}`;
    this.dom.controller.id =
      this.dom.controller.id || `disclosure-controller-${this.key}`;

    this._id = this.dom.disclosure.id;
  }

  /**
   * Sets ARIA attributes throughout the disclosure.
   *
   * The first steps are to ensure that the controlled has `aria-expanded` set to "false"
   * if it's not already explicity set to "true".
   *
   * Then, set the `aria-controls` attribute on the controller to the disclosure's ID.
   *
   * Finally, ensure the controller element has a role of "button" if it is not a native button element.
   *
   * @protected
   */
  _setAriaAttributes() {
    // If the controller element doesn't have aria-expanded set to true, set it to false.
    if (this.dom.controller.getAttribute("aria-expanded") !== "true") {
      this.dom.controller.setAttribute("aria-expanded", "false");
    }

    // Set the aria-controls attribute on the controller to the disclosure's ID.
    this.dom.controller.setAttribute("aria-controls", this.dom.disclosure.id);

    // If the controller element is not a button, set its role to button.
    if (!isTag("button", { controller: this.dom.controller })) {
      this.dom.controller.setAttribute("role", "button");
    }
  }

  /**
   * Sets custom props throughout the disclosure.
   *
   * The custom properties are:
   *   - `--graupl-disclosure-transition-duration`,
   *   - `--graupl-disclosure-open-transition-duration`, and
   *   - `--graupl-disclosure-close-transition-duration`.
   *
   * The prefix of `graupl-` can be changed by setting the disclosure's prefix value.
   *
   * @protected
   */
  _setCustomProps() {
    this.dom.disclosure.style.setProperty(
      `--${this.prefix}disclosure-transition-duration`,
      `${this.transitionDuration}ms`
    );

    this.dom.disclosure.style.setProperty(
      `--${this.prefix}disclosure-open-transition-duration`,
      `${this.openDuration}ms`
    );

    this.dom.disclosure.style.setProperty(
      `--${this.prefix}disclosure-close-transition-duration`,
      `${this.closeDuration}ms`
    );
  }

  /**
   * Sets all DOM elements within the disclosure.
   *
   * Utilizes _setDOMElementType and
   * _resetDOMElementType.
   *
   * @protected
   */
  _setDOMElements() {
    this._resetDOMElementType("content");
    this._setDOMElementType("content", { context: this.dom.disclosure });
  }

  /**
   * Expands the disclosure.
   *
   * Sets the controller's `aria-expanded` to "true", adds the
   * open class to the disclosure, and removes the closed class from the disclosure.
   *
   * @protected
   *
   * @fires grauplDisclosureExpand
   *
   * @param {Object<boolean>} [options = {}]              - Options for expanding the disclosure.
   * @param {boolean}         [options.emit = true]       - Emit the expand event once expanded.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _expand({ emit = true, transition = true } = {}) {
    this.dom.controller.setAttribute("aria-expanded", "true");

    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the close class,
    // add the open class, and finally remove the transition class.
    if (transition && this.transitionlass !== "") {
      addClass(this.transitionClass, this.dom.disclosure);

      requestAnimationFrame(() => {
        removeClass(this.closeClass, this.dom.disclosure);

        requestAnimationFrame(() => {
          addClass(this.openClass, this.dom.disclosure);

          requestAnimationFrame(() => {
            setTimeout(() => {
              removeClass(this.transitionClass, this.dom.disclosure);
            }, this.openDuration);
          });
        });
      });
    } else {
      // Add the open class
      addClass(this.openClass, this.dom.disclosure);

      // Remove the close class.
      removeClass(this.closeClass, this.dom.disclosure);
    }

    this.dom.content.removeAttribute("inert");

    if (emit) {
      this._dispatchEvent("expand", this.dom.controller);
    }
  }

  /**
   * Collapses the disclosure.
   *
   * Sets the controller's `aria-expanded` to "false", adds the
   * close class to the disclosure, and removes the open class from the disclosure.
   *
   * @protected
   *
   * @fires grauplDisclosureCollapse
   *
   * @param {Object<boolean>} [options = {}]              - Options for collapsing the disclosure.
   * @param {boolean}         [options.emit = true]       - Emit the collapse event once collapsed.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _collapse({ emit = true, transition = true } = {}) {
    this.dom.controller.setAttribute("aria-expanded", "false");

    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the open class,
    // add the close class, and finally remove the transition class.
    if (transition && this.transitionClass !== "") {
      addClass(this.transitionClass, this.dom.disclosure);

      requestAnimationFrame(() => {
        removeClass(this.openClass, this.dom.disclosure);

        requestAnimationFrame(() => {
          addClass(this.closeClass, this.dom.disclosure);

          requestAnimationFrame(() => {
            setTimeout(() => {
              removeClass(this.transitionClass, this.dom.disclosure);

              this.dom.content.innert = true;
            }, this.closeDuration);
          });
        });
      });
    } else {
      // Add the close class
      addClass(this.closeClass, this.dom.disclosure);

      // Remove the open class.
      removeClass(this.openClass, this.dom.disclosure);
    }

    this.dom.content.setAttribute("inert", "true");

    if (emit) {
      this._dispatchEvent("collapse", this.dom.controller);
    }
  }

  /**
   * Handles focus events throughout the disclosure.
   *
   * - Adds a `focusout` listener to the disclosure so when the disclosure loses focus it will close.
   */
  _handleFocus() {
    this._addEventListener("focusout", this.dom.disclosure, (event) => {
      if (
        !this.closeOnBlur ||
        this.currentEvent !== "keyboard" ||
        event.relatedTarget === null ||
        this.dom.disclosure.contains(event.relatedTarget) ||
        this.dom.controller === event.relatedTarget
      ) {
        return;
      }

      this.close();
    });
  }

  /**
   * Handles click events throughout the disclosure.
   *
   * - Adds a `pointerup` listener to the controller that toggles the disclosure.
   * - Adds a `pointerup` listener to the `document` so if the user clicks outside the disclosure it will close.
   */
  _handleClick() {
    this._addEventListener("pointerup", this.dom.controller, (event) => {
      this.currentEvent = "mouse";

      if (event.button !== 0) return;

      preventEvent(event);
      this.toggle();
    });

    this._addEventListener("pointerup", document, (event) => {
      if (this.focusState !== "self" || !this.closeOnBlur) return;

      this.currentEvent = "mouse";

      if (
        !this.dom.disclosure.contains(event.target) &&
        this.dom.controller !== event.target
      ) {
        this.close();
      }
    });
  }

  /**
   * Handles keydown events throughout the disclosure.
   *
   * This method exists to assist the _handleKeyup method.
   *
   * - Adds a `keydown` listener to the controller.
   *   - Blocks propagation on "Space" and "Enter" keys.
   * - Adds a `keydown` listener to the disclosure.
   *   - Blocks propagation on "Escape" keys.
   */
  _handleKeydown() {
    this._addEventListener("keydown", this.dom.controller, (event) => {
      this.currentEvent = "keyboard";

      const key = keyPress(event);

      switch (key) {
        case "Space":
        case "Enter":
          preventEvent(event);

          break;
      }
    });

    this._addEventListener("keydown", this.dom.disclosure, (event) => {
      this.currentEvent = "keyboard";

      const key = keyPress(event);

      switch (key) {
        case "Escape":
          preventEvent(event);

          break;
      }
    });
  }

  /**
   * Handles keyup events throughout the disclosure.
   *
   * - Adds a `keyup` listener to the controller.
   *   - Toggles the disclosure on "Space" and "Enter" keys.
   * - Adds a `keyup` listener to the disclosure.
   *   - Closes the disclosure on "Escape" keys.
   */
  _handleKeyup() {
    this._addEventListener("keyup", this.dom.controller, (event) => {
      this.currentEvent = "keyboard";

      const key = keyPress(event);

      switch (key) {
        case "Space":
        case "Enter":
          this.toggle();

          preventEvent(event);

          break;
      }
    });

    this._addEventListener("keyup", this.dom.disclosure, (event) => {
      this.currentEvent = "keyboard";

      const key = keyPress(event);

      switch (key) {
        case "Escape":
          this.close();

          preventEvent(event);

          break;
      }
    });
  }

  /**
   * Opens the disclosure.
   *
   * Sets the disclosure's focus state to "self", calls expand, and sets isOpen to `true`.
   *
   * @param {Object<boolean>} [options = {}]                  - Options for opening the disclosure.
   * @param {boolean}         [options.force = false]         - Whether to force the open action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   */
  open({ force = false, preserveState = false } = {}) {
    if (this.isOpen && !force) return;

    // Set the focus state.
    this.focusState = "self";

    // Expand the disclosure.
    this._expand();

    // Set the open state.
    this._open.value = true;

    if (!preserveState) {
      this._open.commit();
    }
  }

  /**
   * Opens the disclosure without entering it.
   *
   * Sets the disclosure's focus state to "none", calls expand, and sets isOpen to `true`.
   *
   * @param {Object<boolean>} [options = {}]                  - Options for previewing the disclosure.
   * @param {boolean}         [options.force = false]         - Whether to force the preview action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   */
  preview({ force = false, preserveState = false } = {}) {
    if (this.isOpen && !force) return;

    // Set the focus state.
    this.focusState = "none";

    // Expand the disclosure.
    this._expand();

    // Set the open state.
    this._open.value = true;

    if (!preserveState) {
      this._open.commit();
    }
  }

  /**
   * Closes the disclosure.
   *
   * Sets the disclosure's focus state to "none", calls collapse, and sets isOpen to `false`.
   *
   * @param {Object<boolean>} [options = {}]                  - Options for closing the disclosure.
   * @param {boolean}         [options.force = false]         - Whether to force the close action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   */
  close({ force = false, preserveState = false } = {}) {
    if (!this.isOpen && !force) return;

    // Set the focus state.
    this.focusState = "none";

    // Collapse the disclosure.
    this._collapse();

    // Set the open state.
    this._open.value = false;

    if (!preserveState) {
      this._open.commit();
    }
  }

  /**
   * Toggles the open state of the disclosure.
   *
   * @param {Object<boolean>} [options = {}]                  - Options for toggling the disclosure.
   * @param {boolean}         [options.force = false]         - Whether to force the open or close action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   */
  toggle({ force = false, preserveState = false } = {}) {
    if (this.isOpen) {
      this.close({ force, preserveState });
    } else {
      this.open({ force, preserveState });
    }
  }
}

export default Disclosure;
